import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { setIn, useFormikContext } from 'formik'

import { Billing } from '../../model/billing.model'
import { validateIban, setIban, setFields } from '../../store/payments.reducer'
import { selectIban } from '../../store/payments.selectors'

function getModuleDivision(divident: string, divisor: string) {
  let f = divident
  const partLength = 10

  while (f.length > partLength) {
    const part = f.substring(0, partLength)
    f = (parseInt(part, 10) % parseInt(divisor, 10)) + f.substring(partLength)
  }

  return parseInt(f, 10) % parseInt(divisor, 10)
}

function calculateIbanFromAccountNumber(value: string) {
  if (value.length < 20) {
    return ''
  }

  /* We put the first 4 characters ES00 at the end, after CCC. We sustitute the letters for numbers: the E with 14 and the S with 28. */
  const orderedItems = `${value}142800`

  /* We add all the numbers and obtain the remainder of dividing by 97 */
  const sumR = getModuleDivision(orderedItems, '97')

  /* We calculate 98 - R  If the number is less than 10 we add a 0 in front of it */
  const remainder = 98 - sumR

  if (remainder < 10) {
    return `ES0${remainder}`
  }

  return `ES${remainder}`
}

export default function useIban() {
  const IBAN_NOT_VALID = 'Introduzca un IBAN válido'

  const dispatch = useDispatch()
  const iban = useSelector(selectIban)
  const [showAutocompleteIban, setShowAutocompleteIban] = useState(false)
  const {
    values,
    setStatus,
    setFieldError,
    setFieldValue,
    status,
    errors,
  } = useFormikContext<Billing>()

  const trimmedIban = useMemo(() => {
    return values.iban?.replace(/ /g, '') || ''
  }, [values.iban])

  const setFieldsValues = useCallback(() => {
    dispatch(setFields(values))
  }, [setStatus, values])

  useEffect(() => {
    setFieldsValues()
  }, [values])

  useEffect(() => {
    setStatus(setIn(status, `iban.loading`, iban.loading))
    if (iban.isValid || iban.isValid === null) {
      setFieldError('iban', '')
    } else {
      setFieldError('iban', 'El IBAN no es válido (BACK)')
    }
  }, [iban])

  useEffect(() => {
    dispatch(setIban(values.iban))
    const shouldShowCalculateIban = trimmedIban.length === 20 && /^\d+$/.test(trimmedIban)
    setShowAutocompleteIban(shouldShowCalculateIban)
  }, [values.iban])

  function calculateIban() {
    const val = calculateIbanFromAccountNumber(trimmedIban)
    const result = `${val}    ${values.iban}`
    setFieldValue('iban', result)
    validateIbanInput(result)
  }

  function validateIbanInput(value: string) {
    const trimmed = value.replace(/ /g, '')
    if (!value) {
      return 'El IBAN es obligatorio'
    }

    if (value === iban.value) return errors.iban
    if (trimmed.length === 24) {
      dispatch(validateIban(value))
    } else {
      return IBAN_NOT_VALID
    }
    return ''
  }

  return {
    iban: iban.value,
    validateIban: validateIbanInput,
    showAutocompleteIban,
    calculateIban,
  }
}
